import { useState, useEffect, useCallback } from 'react';
import {
  getAllAdmins,
  createAdmin,
  updateAdminRole,
  deactivateAdmin,
  logAdminActivity,
  type AdminUser,
} from '@/shared/db';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';

export function useAdmins() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const adminsData = await getAllAdmins();
      setAdmins(adminsData);
    } catch (err: unknown) {
      console.error('Error fetching admins:', err);
      const errorMessage =
        err instanceof Error ? err.message : t('admin.errors.fetchAdminsFailed');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleCreateAdmin = async (
    userId: string,
    role: 'super_admin' | 'admin' | 'moderator',
    permissions: Record<string, boolean> = {}
  ) => {
    if (!user?.id) {
      toast.error(t('admin.errors.unauthorized'));
      return;
    }

    try {
      const newAdmin = await createAdmin(userId, role, permissions, user.id);
      await logAdminActivity(
        user.id,
        'create_admin',
        'admin',
        userId,
        { role, permissions }
      );
      toast.success(t('admin.admins.actions.created'));
      fetchAdmins();
      return newAdmin;
    } catch (err: unknown) {
      console.error('Error creating admin:', err);
      const errorMessage =
        err instanceof Error ? err.message : t('admin.errors.createAdminFailed');
      toast.error(errorMessage);
      throw err;
    }
  };

  const handleUpdateAdminRole = async (
    adminId: string,
    role: 'super_admin' | 'admin' | 'moderator',
    permissions?: Record<string, boolean>
  ) => {
    if (!user?.id) {
      toast.error(t('admin.errors.unauthorized'));
      return;
    }

    try {
      await updateAdminRole(adminId, role, permissions);
      await logAdminActivity(
        user.id,
        'update_admin_role',
        'admin',
        adminId,
        { role, permissions }
      );
      toast.success(t('admin.admins.actions.updated'));
      fetchAdmins();
    } catch (err: unknown) {
      console.error('Error updating admin role:', err);
      const errorMessage =
        err instanceof Error ? err.message : t('admin.errors.updateAdminFailed');
      toast.error(errorMessage);
      throw err;
    }
  };

  const handleDeactivateAdmin = async (adminId: string) => {
    if (!user?.id) {
      toast.error(t('admin.errors.unauthorized'));
      return;
    }

    try {
      await deactivateAdmin(adminId);
      await logAdminActivity(
        user.id,
        'deactivate_admin',
        'admin',
        adminId,
        { reason: 'Admin action' }
      );
      toast.success(t('admin.admins.actions.deactivated'));
      fetchAdmins();
    } catch (err: unknown) {
      console.error('Error deactivating admin:', err);
      const errorMessage =
        err instanceof Error ? err.message : t('admin.errors.deactivateAdminFailed');
      toast.error(errorMessage);
      throw err;
    }
  };

  return {
    admins,
    loading,
    error,
    createAdmin: handleCreateAdmin,
    updateAdminRole: handleUpdateAdminRole,
    deactivateAdmin: handleDeactivateAdmin,
    refetch: fetchAdmins,
  };
}

